class PrescriptionSelector extends HTMLElement {
  constructor() {
    super()

    // Elements
    this.stepper = this.closest('prescription-glasses-stepper')
    this.stepperContentEl = this.closest('[data-stepper-content]')
    this.subSteps = this.querySelectorAll('[data-substep]')
    this.nextStepButtons = this.querySelectorAll('[data-prescription-next-step]')
    this.submitButtons = this.querySelectorAll('[data-prescription-save-and-finish]')
    this.selectionEl = this.querySelector('[data-prescription-selector-selection]')
    this.form = this.querySelector('[data-prescription-form]')
    this.requiredFields = this.form.querySelectorAll('[required]')
    this.sphereSelects = this.form.querySelectorAll('[data-sphere-select]')
    this.cylinderSelects = this.form.querySelectorAll('[data-cylinder-select]')
    this.addSelects = this.form.querySelectorAll('[data-add-select]')
    this.pdRadios = this.form.querySelectorAll('[data-pd-radio]')
    this.pdFields = this.form.querySelectorAll('[data-pd-field]')
    this.fileInput = this.form.querySelector('[data-file-input]')
    this.fakeFileInput = this.form.querySelector('[data-fake-file-input]')
    this.nicknameInput = this.form.querySelector('#prescription-lens-nickname')
    this.sphereLeftInput = this.form.querySelector('#bs_pre_lens_left_sphere')
    this.sphereRightInput = this.form.querySelector('#bs_pre_lens_right_sphere')
    this.cylinderLeftInput = this.form.querySelector('#bs_pre_lens_left_cyl')
    this.cylinderRightInput = this.form.querySelector('#bs_pre_lens_right_cyl')
    this.axisLeftInput = this.form.querySelector('#bs_pre_lens_left_axis')
    this.axisRightInput = this.form.querySelector('#bs_pre_lens_right_axis')
    this.addLeftInput = this.form.querySelector('#bs_pre_lens_left_add')
    this.addRightInput = this.form.querySelector('#bs_pre_lens_right_add')

    this.binoRadio = this.form.querySelector('#binocular')
    this.pdBothInput = this.form.querySelector('#bs_pre_lens_both_pup_dist')
    this.pdLeftInput = this.form.querySelector('#bs_pre_lens_left_pup_dist')
    this.pdRightInput = this.form.querySelector('#bs_pre_lens_right_pup_dist')

    this.addFieldset = this.form.querySelector('[data-add-fieldset]')
    this.noAddEl = this.form.querySelector('[data-no-add-fieldset]')
    this.resetButton = this.form.querySelector('[data-restart-stepper]')

    this.termsCheckbox = this.form.querySelector('#terms')

    // Data
    this.prescriptionsData = []
    this.currentStep = 1

    this.init()
  }

  async init () {
    try {
      await this.getPrescriptionsData()
      this.createPrescriptionOptions()
    } catch (error) {
      console.error('Error: something went wrong with the PrescriptionSelector element', error)
    }

    this.addFormSelectOptions()
    this.initPDTypeToggle()
    this.initFileInput()
    this.initStepButtons()
    this.initSubmitButtons()
    this.initResetButton()
    this.initLoginEvent()
  }

  initLoginEvent () {
    window.addEventListener('login', () => {
      this.resetRXOptions()
    })
  }

  async resetRXOptions () {
    const currentRxOptions = this.querySelectorAll('[data-rx-option]')
    currentRxOptions.forEach(currentRxOption => currentRxOption.remove())

    try {
      await this.getPrescriptionsData()
      this.createPrescriptionOptions()
    } catch (error) {
      console.error('Error: something went wrong with the PrescriptionSelector element', error)
    }
  }

  changeStep (dir) {
    const newStep = this.currentStep + dir
    if (newStep <= 0) { return }

    this.currentStep = newStep
    this.subSteps.forEach(subStep => {
      subStep.classList.toggle('hidden', subStep.dataset.substep !== newStep.toString())
      if (subStep.dataset.substep !== newStep.toString()) { return }
      this.activeStepEl = subStep
    })

    this.stepperContentEl.scrollTop = 0
    setTimeout(() => {
      this.stepper.classList.toggle('scrollable', this.activeStepEl.offsetHeight > this.stepperContentEl.offsetHeight)
    }, 150)
  }

  async getPrescriptionsData () {
    const isCustomer = window.theme.customer
    if (isCustomer) {
      const customerPrescriptions = await this.getCustomerPrescriptionsData()
      if (customerPrescriptions.prescriptions.length) {
        const rxOptions = customerPrescriptions.prescriptions.sort(function (a, b) {
          const keyA = new Date(a.prescriptionDate)
          const keyB = new Date(b.prescriptionDate)
          if (keyA > keyB) return -1;
          if (keyA < keyB) return 1;
          return 0;
        })
        this.prescriptionsData.push(...rxOptions)
        customerPrescriptions.prescriptions.forEach(prescription => cartPrescriptions.addPrescription('S' + prescription.spectaclePrescriptionID, prescription.spectaclePrescriptionID, 'Frames'))
      }
    }

    const guestPrescriptions = cartPrescriptions.prescriptions.sort(function (a, b) {
      const keyA = new Date(a.prescriptionDate)
      const keyB = new Date(b.prescriptionDate)
      if (keyA > keyB) return -1;
      if (keyA < keyB) return 1;
      return 0;
    })
    .filter(rx => rx.id && rx.id.includes('guest_') && rx.hasOwnProperty('spectaclePrescription'))
    .map(rx => rx.spectaclePrescription)
    guestPrescriptions.length && this.prescriptionsData.push(...guestPrescriptions)
  }

  async getCustomerPrescriptionsData () {
    try {
      const res = await fetch(`${window.apiUrl}/prescriptions/spectacle`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem('LRTokenKey')}`
        }
      })

      if (!res.ok) { throw new Error(res.statusText) }
      return await res.json()
    } catch (err) {
      console.error(err)
    }
  }

  addFormSelectOptions () {
    this.sphereSelects.forEach(sphereSelect => {
      window.LENS_SPH_VALS.forEach(option => {
        const optionEl = document.createElement('option')
        optionEl.value = option.value
        optionEl.innerHTML = option.text

        sphereSelect.appendChild(optionEl)
      })
    })

    this.cylinderSelects.forEach(cylinderSelect => {
      window.LENS_CYL_VALS.forEach(option => {
        const optionEl = document.createElement('option')
        optionEl.value = option.value
        optionEl.innerHTML = option.text

        cylinderSelect.appendChild(optionEl)
      })
    })

    this.addSelects.forEach(addSelect => {
      window.LENS_ADD_VALS.forEach(option => {
        const optionEl = document.createElement('option')
        optionEl.value = option.value
        optionEl.innerHTML = option.text

        addSelect.appendChild(optionEl)
      })
    })
  }

  initPDTypeToggle () {
    this.pdRadios.forEach(pdRadio => {
      pdRadio.addEventListener('change', () => {
        this.pdFields.forEach(pdField => {
          pdField.classList.toggle('hidden', !(pdRadio.checked && pdRadio.value === pdField.dataset.pdField))
        });
      })
    })
  }

  createPrescriptionOptions () {
    this.prescriptionsData.reverse().forEach((prescription, index) => {
      const el = document.createElement('div')
      el.setAttribute('data-rx-option', true)
      el.innerHTML = this.buildPrescriptionEl(prescription)
      const input = el.querySelector('[data-prescription-input]')
      this.addEventToPrescriptionInput(input)
      this.selectionEl.prepend(el)
    })
  }

  buildPrescriptionEl(prescription) {
    const nearAdd = Boolean(!(
      prescription.osSpectacleLens.nearAdd === '' ||
      prescription.osSpectacleLens.nearAdd === null ||
      prescription.osSpectacleLens.nearAdd === 0 ||
      prescription.odSpectacleLens.nearAdd === '' ||
      prescription.odSpectacleLens.nearAdd === null ||
      prescription.odSpectacleLens.nearAdd === 0
    ))
    const prescriptionId = prescription.spectaclePrescriptionID ? `S${prescription.spectaclePrescriptionID}` : prescription.id
    return `
      <input data-prescription-input type="radio" id="prescription-${prescriptionId}" name="prescription" value="${prescriptionId}" class="visually-hidden" data-near-add="${nearAdd}">
      <label class="prescription-glasses-stepper__saved-prescription" for="prescription-${prescriptionId}">
        <p class="prescription-glasses-stepper__saved-prescription-name">
          ${prescription.nickname ? prescription.nickname : ''}
        </p>
        <div class="prescription-glasses-stepper__saved-prescription-details">
          <div class="prescription-glasses-stepper__saved-prescription-eyes">
            <p><strong>${window.prescriptions.text.right_eye_od}</strong></p>
            <p class="muted">
              ${window.prescriptions.text.od}&nbsp;
              ${prescription.odSpectacleLens.sphere}&nbsp;
              ${prescription.odSpectacleLens.cylinder ? prescription.odSpectacleLens.cylinder : '0'}&nbsp;
              ${prescription.odSpectacleLens.axis ? prescription.odSpectacleLens.axis : '0'}&nbsp;
              ${prescription.odSpectacleLens.baseCurve ? prescription.odSpectacleLens.baseCurve : '0'}&nbsp;
              ${prescription.odSpectacleLens.nearAdd ? prescription.odSpectacleLens.nearAdd : '0'}
            </p>
            <br>
            <p><strong>${window.prescriptions.text.left_eye_os}</strong></p>
            <p class="muted">
              ${window.prescriptions.text.os}&nbsp;
              ${prescription.osSpectacleLens.sphere}&nbsp;
              ${prescription.osSpectacleLens.cylinder ? prescription.osSpectacleLens.cylinder : '0'}&nbsp;
              ${prescription.osSpectacleLens.axis ? prescription.osSpectacleLens.axis : '0'}&nbsp;
              ${prescription.osSpectacleLens.baseCurve ? prescription.osSpectacleLens.baseCurve : '0'}&nbsp;
              ${prescription.osSpectacleLens.nearAdd ? prescription.osSpectacleLens.nearAdd : '0'}
            </p>
          </div>
          <div class="prescription-glasses-stepper__saved-prescription-dates">
            <p><span class="muted">${window.prescriptions.text.issued}</span><strong>${this.renderDate(prescription.prescriptionDate)}</strong></p>
            <p><span class="muted">${window.prescriptions.text.expires}</span><strong>${this.renderDate(prescription.expiryDate)}</strong></p>
          </div>
        </div>
      </label>
    `
  }

  renderDate (prescriptionDate) {
    if (!prescriptionDate) return `-`
    const date = new Date(prescriptionDate)
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
  }

  initFileInput () {
    this.fileInput.addEventListener('change', () => {
      this.fakeFileInput.innerHTML = this.fileInput.value.replace(/C:\\fakepath\\/i, '')
    })

    this.fakeFileInput.addEventListener('click', () => {
      this.fileInput.click()
    })
  }

  initStepButtons () {
    this.nextStepButtons.forEach(nextStepButton => {
      nextStepButton.addEventListener('click', () => {
        if (nextStepButton.hasAttribute('data-prescription-validate')) {
          const valid = this.validateForm()
          if (!valid) { return }
        }
        this.changeStep(1)
        window.scrollTo(0, 0)
      })
    })
  }

  validateForm () {
    let valid = true
    this.requiredFields.forEach(requiredField => {
      const isValid = requiredField.type === 'checkbox' ? requiredField.checked : (requiredField.value !== '')
      requiredField.classList.toggle('has-error', !isValid)
      if (!isValid) valid = false
    })

    return valid
  }

  initSubmitButtons () {
    this.submitButtons.forEach(submitButton => {
      submitButton.addEventListener('click', (event) => {
        event.preventDefault()
        this.createFormData()
        window.scrollTo(0, 0)
      })
    });
  }

  resetForm () {
    this.subSteps.forEach(subStep => {
      subStep.classList.toggle('hidden', subStep.dataset.substep !== '1')
    })

    const fields = this.form.querySelectorAll('input, select')
    fields.forEach(field => {
      if (field.type === 'checkbox') {
        field.checked = false
        return
      } else if (field.id === 'binocular') {
        field.checked = true
        const event = new Event('change');
        field.dispatchEvent(event)
        return
      }

      field.value = ''
    })

    this.currentStep = 1

    this.submitButtons.forEach(submitButton => {
      submitButton.disabled = false
    });
  }

  async createFormData () {
    this.submitButtons.forEach(submitButton => {
      submitButton.disabled = true
    })

    const source = Shopify.shop.includes('visique') ? 9 : 8
    const now = new Date
    const formattedDate = now.toISOString()
    const prescriptionFormData = {
      "customerFirstName": "",
      "customerLastName": "",
      "active": 1,
      "source": source,
      "uploadedDate": formattedDate,
      "prescriptionDate": formattedDate,
      "expiryDate": formattedDate,
      "doctorID": null,
      "doctorPrefix": "",
      "doctorSuffix": "",
      "doctorFirstName": "",
      "doctorLastName": "",
      "doctorJobCode": "",
      "clinicID": null,
      "clinicName": "",
      "clinicCompany": "",
      "clinicAddress": {
        "active": 0,
        "addressType": "",
        "addressLine1": "",
        "addressLine2": "",
        "addressLine3": "",
        "town": "",
        "province": "",
        "country": "",
        "postalCode": ""
      },
      "clinicEmail": "",
      "clinicPhoneNumber": "",
      "clinicFaxNumber": "",
      "nickname": this.nicknameInput.value !== '' ? this.nicknameInput.value : 'Manually Added Prescription',
      "odSpectacleLens": {
        "lensMaker": "",
        "lensModel": "",
        "baseCurve": null,
        "diameter": 0,
        "sphere": this.sphereRightInput.value,
        "cylinder": this.cylinderRightInput.value,
        "axis": this.axisRightInput.value,
        "add": 0,
        "nearAdd": this.addRightInput.value,
        "alphaAdd": "",
        "lensMaterial": "",
        "lensColour": "",
        "productSku": ""
      },
      "osSpectacleLens": {
        "lensMaker": "",
        "lensModel": "",
        "baseCurve": 0,
        "diameter": 0,
        "sphere": this.sphereLeftInput.value,
        "cylinder": this.cylinderLeftInput.value,
        "axis": this.axisLeftInput.value,
        "add": 0,
        "nearAdd": this.addLeftInput.value,
        "alphaAdd": "",
        "lensMaterial": "",
        "lensColour": "",
        "productSku": ""
      },
      "prescriptionNotes": "",
      "file": "",
      "validated": false,
      "validationStatus": 1,
      "validationNotes": ""
    }

    if (this.binoRadio.checked) {
      prescriptionFormData.binocularDistPD = this.pdBothInput.value
    } else {
      prescriptionFormData.odSpectacleLens.monoDistPD = this.pdRightInput.value
      prescriptionFormData.osSpectacleLens.monoDistPD = this.pdLeftInput.value
    }

    let fileGUID = ''
    if (this.fileInput.files.length > 0) {
      const data = new FormData()
      data.append('file', this.fileInput.files[0])

      try {
        const response = await fetch(window.apiUrl + '/upload', {
          method: 'POST',
          body: data,
          cache: 'no-cache',
          headers: {
          }
        })

        if (!response.ok) {
          throw new Error('Network response was not ok ' + response.statusText)
        }

        const fileReq = await response.json()
        fileGUID = fileReq.file.fileGUID
      } catch (error) {
        console.error('There was a problem with the fetch operation:', error)
      }
    }

    prescriptionFormData.file = fileGUID
    if(window.theme.customer) {
      this.customerFormSubmit(prescriptionFormData)
      return
    }

    this.guestFormSubmit(prescriptionFormData, new Date().getTime())
  }

  async customerFormSubmit (data) {
    const token = localStorage.getItem('LRTokenKey')

    const handleGetSuccess = (response) => {
      const prescriptionsOrderedByDate = response.prescriptions.sort((a, b) => new Date(b.prescriptionDate) - new Date(a.prescriptionDate)).reverse()
      const prescriptionIndex = prescriptionsOrderedByDate.length - 1
      const prescription = prescriptionsOrderedByDate[prescriptionIndex]
      this.addNewPrescriptionToList(prescription)
      this.resetForm()
    }

    const handleSuccess = async (response) => {
      await getPrescription()

      if (response.odSpectacleLens.nearAdd !== "" && response.odSpectacleLens.nearAdd !== null) {
        localStorage.setItem("framesNear", "true");
      }
      if (response.osSpectacleLens.nearAdd !== "" && response.osSpectacleLens.nearAdd !== null) {
        localStorage.setItem("framesNear", "true");
      }
      if (response.odSpectacleLens.nearAdd === null) {
        localStorage.setItem("framesNear", "false");
      }
      if (response.osSpectacleLens.nearAdd === null) {
        localStorage.setItem("framesNear", "false");
      }

      const prescriptionId = "S" + response.spectaclePrescriptionID;
      localStorage.setItem("currentPrescriptionID", prescriptionId);
      const properties = {
        "prescriptionID": prescriptionId,
        "nearVisionLenses": localStorage.getItem("framesNear")
      }
      const prescriptionEl = document.querySelector('[data-product-prescription]')
      if (prescriptionEl) { prescriptionEl.innerHTML = JSON.stringify(properties) }
    }

    const getPrescription = async () => {
      try {
        const response = await fetch(`${window.apiUrl}/prescriptions/spectacle`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const res = await response.json();
        handleGetSuccess(res);
      } catch (error) {
        console.error('Error:', error);
      }
    }

    try {
      const response = await fetch(`${window.apiUrl}/prescriptions/spectacle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const res = await response.json();
      handleSuccess(res);
    } catch (error) {
      console.error('Error:', error);
    }
  }

  addNewPrescriptionToList (prescription) {
    const el = document.createElement('div')
    el.innerHTML = this.buildPrescriptionEl(prescription)
    const input = el.querySelector('[data-prescription-input]')
    this.addEventToPrescriptionInput(input)
    this.selectionEl.prepend(el)
    input.checked = true
    this.stepper.setStepNumber(this.stepper.totalSteps)
    this.stepper.updateStepperData()
  }

  addEventToPrescriptionInput (input) {
    input.addEventListener('click', () => {
      this.proceedToTotal()
    })
  }

  proceedToTotal () {
    this.stepper.setStepNumber(this.stepper.totalSteps)
    window.scrollTo(0, 0)
  }

  guestFormSubmit (data, now) {
    const id = `guest_${now}`
    localStorage.setItem('currentPrescriptionID', id)
    data.id = id
    window.cartPrescriptions.addPrescription(id, data, 'Frames')

    this.addNewPrescriptionToList(data)
    this.resetForm()

    const properties = {
      "prescriptionID": id,
      "nearVisionLenses": localStorage.getItem('framesNear')
    }
    const prescriptionEl = document.querySelector('[data-product-prescription]')
    if (prescriptionEl) { prescriptionEl.innerHTML = JSON.stringify(properties) }
  }

  updateFormBasedOnLens (type) {
    this.addFieldset.classList.toggle('hidden', type !== 'Has Add')
    this.noAddEl.classList.toggle('hidden', type === 'Has Add')
  }

  initResetButton () {
    this.resetButton.addEventListener('click', () => {
      this.stepper.setStepNumber(1)
      this.resetForm()
    })
  }
}

customElements.define('prescription-selector', PrescriptionSelector)
